import React, { useEffect, useRef, useState } from 'react';
import { useIsForeground } from '../../services/AppStateService';
import { PhotoIcon, ChartBarIcon, ClipboardDocumentListIcon, Squares2X2Icon, ArrowPathIcon, ClockIcon } from '@heroicons/react/24/outline';
import { todayActivityId, todayActivityBucket, todayActionBuckets } from './todayNextAction';
import ClassicServingIcon from './ClassicServingIcon';
import DiagonalDumbbellIcon from './DiagonalDumbbellIcon';
import useWorkoutShortcutVisibility from './useWorkoutShortcutVisibility';
import { isOngoingWorkoutActivity } from '../../utils/activityStatus';
import './TopBarActivityActions.css';

const icons = { workout: DiagonalDumbbellIcon, meal: ClassicServingIcon, media: PhotoIcon, metric: ChartBarIcon, survey: ClipboardDocumentListIcon };
const localDateKey = value => new Date(value).toDateString();
export function activityDeadlineLabel(activity, now, getDateKey = localDateKey) {
  if (activity?.status === 'complete') return { short: 'Done', full: 'Completed', urgency: 'neutral' };
  const due = activity?.due_at ? Date.parse(activity.due_at) : NaN;
  if (!Number.isFinite(due) || !Number.isFinite(now)) return { short: 'Today', full: 'Available today; no deadline', urgency: 'neutral' };
  if (due < now) return { short: 'Overdue', full: 'Overdue', urgency: 'overdue' };
  if (due === now) return { short: 'Due now', full: 'Due now', urgency: 'urgent' };
  const minutes = Math.ceil((due - now) / 60000);
  const short = minutes >= 60 ? `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ''}` : `${minutes}m`;
  const remaining = due - now;
  // Compare exact milliseconds before rounding the minute-only readout.
  const urgency = remaining < 3 * 3600000 ? 'urgent' : remaining < 6 * 3600000 ? 'soon'
    : getDateKey(due) === getDateKey(now) ? 'today' : 'future';
  return { short, full: `Due in ${short}`, urgency };
}

const actionKey = action => action.activity ? todayActivityId(action.activity) : action.kind;
const bucket = action => todayActivityBucket(action.activity);
function useReducedMotion() {
  const [reduced, setReduced] = useState(() => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches));
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(Boolean(query?.matches));
    query?.addEventListener('change', update);
    return () => query?.removeEventListener('change', update);
  }, []);
  return reduced;
}

/** Navigation-only projection of Activities' canonical unfiltered Today scope.
 * completedIds must come from a ready, complete, same-viewer/day snapshot.
 * The parent keys this component by viewer/day to discard old transition state.
 */
export default function TopBarActivityActions({ actions = [], completedIds = [], ongoingWorkoutId = null, now = Date.now(), getDateKey, loading = false, locked = false, refreshing = false, onAction }) {
  const foreground = useIsForeground();
  const reduced = useReducedMotion();
  const activeId = !loading && actions.some(action => action.kind === 'resume'
    && todayActivityId(action.activity) === ongoingWorkoutId && isOngoingWorkoutActivity(action.activity, now)) ? ongoingWorkoutId : null;
  const workoutVisible = useWorkoutShortcutVisibility(activeId, foreground);
  const signature = JSON.stringify([actions.map(actionKey), completedIds, foreground, reduced]);
  const [display, setDisplay] = useState(() => ({ signature, rows: actions.map(action => ({ action })) }));
  const latest = useRef(actions);
  latest.current = actions;
  if (display.signature !== signature) {
    const exits = foreground && !reduced ? display.rows.filter(row => row.action.activity
      && !actions.some(action => actionKey(action) === actionKey(row.action))
      && completedIds.includes(actionKey(row.action))).map(row => ({ ...row, completing: true })) : [];
    const rows = [...exits, ...actions.filter(action => !exits.some(row => bucket(row.action) === bucket(action))
      && !(exits.length && action.kind === 'complete')).map(action => ({ action, celebrating: action.kind === 'complete'
        && display.rows.length > 0 && !display.rows.some(row => row.action.kind === 'complete') && foreground && !reduced }))];
    // Retiring identities keep their canonical bucket position until removed.
    if (exits.length) rows.sort((a, b) => todayActionBuckets.indexOf(bucket(a.action))
      - todayActionBuckets.indexOf(bucket(b.action)));
    setDisplay({ signature, rows });
  }
  const exitKey = display.rows.filter(row => row.completing).map(row => actionKey(row.action)).join('|');
  useEffect(() => {
    if (!exitKey) return undefined;
    const timer = setTimeout(() => setDisplay(previous => ({ ...previous, rows: latest.current.map(action => ({ action,
      celebrating: action.kind === 'complete' && foreground && !reduced })) })), 650);
    return () => clearTimeout(timer);
  }, [exitKey, foreground, reduced]);
  const celebrating = display.rows.some(row => row.celebrating);
  useEffect(() => {
    if (!celebrating) return undefined;
    const timer = setTimeout(() => setDisplay(previous => ({ ...previous,
      rows: previous.rows.map(row => row.celebrating ? { ...row, celebrating: false } : row) })), 700);
    return () => clearTimeout(timer);
  }, [celebrating]);
  // Unknown is not an empty day or a pending count. Keep this cue static, including
  // under reduced motion, and separate from the actionable retry/setup states.
  if (loading) return <section className="topbar-activities" aria-label="Today's activity shortcuts">
    <span className="topbar-activities-loading" role="status" aria-live="polite" aria-label="Checking today's activities">Checking…</span>
  </section>;
  if (!display.rows.length) return null;
  return <section className="topbar-activities" data-motion={foreground && !reduced ? 'allowed' : 'off'} aria-label="Today's activity shortcuts">
    {display.rows.map(row => {
      const action = row.completing ? row.action : actions.find(item => actionKey(item) === actionKey(row.action)) || row.action;
      const completing = Boolean(row.completing);
      if (action.kind === 'complete') return <span key="complete" className={`topbar-activities-complete${row.celebrating ? ' is-celebrating' : ''}`} role="img" aria-label="All planned activities complete">
        <span className="topbar-complete-orbit" aria-hidden="true" />
        <svg className="topbar-complete-check" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>;
      const activity = action.activity;
      const Icon = icons[activity?.type] || (action.kind === 'error' || action.kind === 'partial' ? ArrowPathIcon : Squares2X2Icon);
      const deadline = activityDeadlineLabel(activity, now, getDateKey);
      const urgency = completing ? 'neutral' : deadline.urgency;
      const showClock = activity && !['neutral', 'future'].includes(urgency);
      const readout = completing ? 'Done' : activity ? deadline.short : action.kind === 'setup' ? 'Plan' : action.label;
      const ongoing = !completing && Boolean(activeId) && todayActivityId(activity) === activeId;
      const label = activity ? `${action.label}: ${activity.name}. ${completing ? 'Completed' : deadline.full}${ongoing ? '. Workout ongoing' : ''}` : action.title;
      return <button key={actionKey(action)} type="button" className={`topbar-activity${completing ? ' is-completing' : ''}`} data-activity-type={activity?.type} data-urgency={urgency} data-workout-cue={ongoing ? (workoutVisible === false ? 'away' : 'visible') : undefined} disabled={completing || locked || refreshing} aria-label={label} title={label} onClick={() => { if (!completing) onAction?.(action); }}>
        {ongoing && <span className="topbar-workout-border" aria-hidden="true" />}
        <Icon aria-hidden="true" />{showClock && <ClockIcon className="topbar-activity-clock" aria-hidden="true" />}
        <span key={readout} className="topbar-activity-deadline">{readout}</span>
      </button>;
    })}
  </section>;
}
